import React, { useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import SidewaysLoading from './sidebar/SidewaysLoading';
import { ReactComponent as MicIcon } from '../../icons/mic.svg';
import './VoiceRecorder.scss';

interface VoiceRecorderProps {
  onTranscriptionDone: (transcription: string) => void;
}

type RecorderState = 'idle' | 'recording' | 'uploading' | 'transcribing';

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  onTranscriptionDone,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [state, setState] = useState<RecorderState>('idle');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null);

  // Helper to get supported MIME type
  const getMimeType = (): string => {
    const types = [
      'audio/webm',
      'audio/mp4',
      'audio/ogg',
      'audio/wav',
      'audio/aac',
    ];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return 'audio/webm'; // fallback
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const mimeType = getMimeType();
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        await handleRecordingStop();
      };

      mediaRecorder.start();
      setState('recording');
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Error accessing microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && state === 'recording') {
      mediaRecorderRef.current.stop();

      // Stop all audio tracks
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    }
  };

  const handleRecordingStop = async () => {
    setState('uploading');

    try {
      const mimeType = mediaRecorderRef.current?.mimeType || 'audio/webm';
      const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });

      // Prepare form data
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      setState('transcribing');

      // Get auth token
      const token = await getAccessTokenSilently();

      // Send to backend
      const response = await api.post('/voice-transcribe', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success && response.data.transcription) {
        onTranscriptionDone(response.data.transcription);
      } else {
        throw new Error('Transcription failed');
      }

      setState('idle');
    } catch (error) {
      console.error('Error transcribing audio:', error);
      alert('Error transcribing audio. Please try again.');
      setState('idle');
    }
  };

  const handleButtonClick = () => {
    if (state === 'idle') {
      startRecording();
    } else if (state === 'recording') {
      stopRecording();
    }
  };

  const getButtonContent = () => {
    switch (state) {
      case 'idle':
        return <MicIcon />;
      case 'recording':
        return <MicIcon />;
      case 'uploading':
        return <SidewaysLoading />;
      case 'transcribing':
        return <SidewaysLoading />;
      default:
        return <MicIcon />;
    }
  };

  const isDisabled = state === 'uploading' || state === 'transcribing';

  return (
    <button
      onClick={handleButtonClick}
      disabled={isDisabled}
      className={`voice-recorder-button ${state === 'recording' ? 'recording' : ''}`}
    >
      {getButtonContent()}
    </button>
  );
};

export default VoiceRecorder;
